import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import Loading from "../../../../../components/Loading/Loading";
import localeText from "../../../../../locale";
import { EXTRAPAY_PATH } from "./constants";
import useExtrapayOverview from "./hooks/useExtrapayOverview";
import useExtrapayPurchases from "./hooks/useExtrapayPurchases";
import useExtrapayWithdrawals from "./hooks/useExtrapayWithdrawals";
import useExtrapayConversions from "./hooks/useExtrapayConversions";
import { getExcStatementConversions } from "./mapConversions";
import ExtrapayBreadcrumb from "./components/ExtrapayBreadcrumb";
import ExtrapayPageHeader from "./components/ExtrapayPageHeader";
import { ConversionRateNotice, ExcBalanceCard, PointsSummaryCard } from "./components/ExtrapaySummaryCards";
import PointsStatementTable from "./components/PointsStatementTable";
import ExcStatementTable from "./components/ExcStatementTable";
import WithdrawalsStatementTable from "./components/WithdrawalsStatementTable";

const TABS = [
  { id: "pontos", labelKey: "extrapayFlowTabPoints" },
  { id: "exc", labelKey: "extrapayFlowTabExc" },
  { id: "saques", labelKey: "extrapayFlowTabWithdrawals" },
];

const ExtrapayPage = () => {
  const history = useHistory();
  const location = useLocation();
  const overview = useExtrapayOverview();
  const purchases = useExtrapayPurchases({
    cycle: overview.cycle,
    ordersInCycle: overview.ordersInCycle,
    enabled: !overview.isLoading && !overview.hasError,
  });
  const withdrawals = useExtrapayWithdrawals();
  const conversions = useExtrapayConversions();

  const activeTab = useMemo(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    return TABS.some((item) => item.id === tab) ? tab : "pontos";
  }, [location.search]);

  const selectTab = (tab) => {
    history.replace(`${EXTRAPAY_PATH}?tab=${tab}`);
  };

  return (
    <DashboardLayout>
      <ExtrapayBreadcrumb
        items={[{ labelKey: "financial", to: "/dashboard-financeiro" }, { labelKey: "extrapayFlowTitle" }]}
      />
      <ExtrapayPageHeader
        title={localeText("extrapayFlowTitle")}
        subtitle={localeText("extrapayFlowPageSubtitle")}
      />

      {overview.isLoading ? (
        <div className="flex justify-content-center py-6">
          <Loading classNameIcon="text-6xl" />
        </div>
      ) : overview.hasError ? (
        <p className="text-sm" style={{ color: "var(--theme-text)" }}>
          {localeText("extrapayFlowLoadError")}
        </p>
      ) : (
        <>
          <div className="grid">
            <div className="col-12 lg:col-6">
              <PointsSummaryCard
                pointsBalance={overview.pointsBalance}
                ordersInCycle={overview.ordersInCycle}
                cycle={overview.cycle}
              />
            </div>
            <div className="col-12 lg:col-6">
              <ExcBalanceCard excBalance={overview.excBalance} />
            </div>
          </div>
          <ConversionRateNotice conversionRate={overview.conversionRate} />

          <div className="flex gap-4 mt-4" style={{ borderBottom: "1px solid var(--theme-border, #EBEBEB)" }}>
            {TABS.map((tab) => {
              const selected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className="bg-transparent border-none cursor-pointer px-0 pb-2 text-sm"
                  style={{
                    color: selected ? "#335CFF" : "var(--theme-text-muted, #5C5C5C)",
                    borderBottom: selected ? "2px solid #335CFF" : "2px solid transparent",
                    marginBottom: "-1px",
                  }}
                  onClick={() => selectTab(tab.id)}
                >
                  {localeText(tab.labelKey)}
                </button>
              );
            })}
          </div>

          <div className="mt-3">
            {activeTab === "pontos" && (
              <PointsStatementTable purchases={purchases.purchases} isLoading={purchases.isLoading} />
            )}
            {activeTab === "exc" && (
              <ExcStatementTable
                conversions={getExcStatementConversions(conversions.conversions)}
                isLoading={conversions.isLoading}
              />
            )}
            {activeTab === "saques" && (
              <WithdrawalsStatementTable
                withdrawals={withdrawals.withdrawals}
                isLoading={withdrawals.isLoading}
              />
            )}
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default ExtrapayPage;
